import * as React from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import { useTranslate } from "react-admin";

const numericSources = new Set([
  "sale_net_g",
  "sale_net_phm",
  "sale_net_z",
  "sale_net_total",
  "gross_incentive_g",
  "gross_incentive_phm",
  "gross_incentive_z",
  "gross_incentive_total",
  "penalty",
  "net_incentive",
  "final_incentive",
  "profit_ratio",
  "profit_multiplier",
]);

const sourceConfig = {
  "company.alias": {
    groupLabel: "Salesman",
    shortLabel: "Company",
    align: "left",
    width: 170,
  },
  name: {
    groupLabel: "Salesman",
    shortLabel: "Nama Salesman",
    align: "left",
    width: 230,
  },
  sale_net_g: { groupLabel: "Nilai Penjualan Net (A)", shortLabel: "G", width: 130 },
  sale_net_phm: { groupLabel: "Nilai Penjualan Net (A)", shortLabel: "PHM", width: 130 },
  sale_net_z: { groupLabel: "Nilai Penjualan Net (A)", shortLabel: "Z", width: 130 },
  sale_net_total: {
    groupLabel: "Nilai Penjualan Net (A)",
    shortLabel: "Total",
    width: 140,
  },
  incentive_percentage_g: { groupLabel: "% Insentif (B)", shortLabel: "G", width: 90 },
  incentive_percentage_phm: {
    groupLabel: "% Insentif (B)",
    shortLabel: "PHM",
    width: 90,
  },
  incentive_percentage_z: { groupLabel: "% Insentif (B)", shortLabel: "Z", width: 90 },
  gross_incentive_g: {
    groupLabel: "Nilai Insentif (C=A*B)",
    shortLabel: "G",
    width: 130,
  },
  gross_incentive_phm: {
    groupLabel: "Nilai Insentif (C=A*B)",
    shortLabel: "PHM",
    width: 130,
  },
  gross_incentive_z: {
    groupLabel: "Nilai Insentif (C=A*B)",
    shortLabel: "Z",
    width: 130,
  },
  gross_incentive_total: {
    groupLabel: "Nilai Insentif (C=A*B)",
    shortLabel: "Total",
    width: 140,
  },
  penalty: { groupLabel: "Penalti (D)", shortLabel: "D", width: 130 },
  net_incentive: {
    groupLabel: "Insentif - Penalti (E=C-D)",
    shortLabel: "E",
    width: 190,
  },
  final_incentive: {
    groupLabel: "Insentif Diterima (E*G)",
    shortLabel: "E*G",
    width: 190,
  },
  profit_ratio: { groupLabel: "Rasio Profit (F)", shortLabel: "F", width: 130 },
  profit_multiplier: { groupLabel: "F. Pengali (G)", shortLabel: "G", width: 90 },
};

const ratioTooltipText =
  "Rasio Profit dihitung global per periode: Laba/Rugi Bersih Setelah Pajak dibagi (Penjualan Bersih + Tax Out - (Tax Out - Tax In)).";

const getGroupWidth = (group) =>
  group.columns.reduce((total, column) => total + (column.width || 120), 0);

const SalesmanIncentiveHeader = ({ children, hasCompanyColumn = false }) => {
  const translate = useTranslate();

  const columns = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child) => {
      const source = child.props.source;
      const config = sourceConfig[source] || {};
      const rawLabel = child.props.label;

      let translatedLabel = "";
      if (typeof rawLabel === "string") {
        translatedLabel = translate(rawLabel);
      }

      const shortLabel =
        config.shortLabel || translatedLabel || source || "Column";

      return {
        source,
        align:
          config.align ||
          (numericSources.has(source) ? "right" : child.type?.textAlign || "left"),
        shortLabel,
        groupLabel: config.groupLabel || shortLabel,
        headerClassName: child.props.headerClassName,
        width: config.width,
      };
    });

  const groupedColumns = columns.reduce((acc, column) => {
    const last = acc[acc.length - 1];

    if (last && last.label === column.groupLabel) {
      last.colSpan += 1;
      last.columns.push(column);
      return acc;
    }

    acc.push({
      label: column.groupLabel,
      colSpan: 1,
      columns: [column],
    });
    return acc;
  }, []);

  const subheaderColumns = groupedColumns.flatMap((group) =>
    group.colSpan > 1 ? group.columns : []
  );

  return (
    <TableHead
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 6,
        "& .MuiTableCell-root": {
          whiteSpace: "nowrap",
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
        },
        "& .si-group-row .MuiTableCell-root": {
          py: 0.75,
          fontWeight: 700,
          backgroundColor: "#f4f4f6",
        },
        "& .si-group-sticky-salesman": {
          position: "sticky",
          left: 0,
          zIndex: 8,
          minWidth: hasCompanyColumn ? 400 : 230,
          width: hasCompanyColumn ? 400 : 230,
          borderRight: "1px solid rgba(224, 224, 224, 1)",
          backgroundColor: "#f4f4f6",
        },
        "& .si-subheader-row .MuiTableCell-root": {
          py: 0.5,
          backgroundColor: (theme) => theme.palette.background.default,
        },
      }}
    >
      <TableRow className="si-group-row">
        {groupedColumns.map((group, index) => {
          const hasProfitRatio = group.columns.some(
            (column) => column.source === "profit_ratio"
          );
          const isSalesmanGroup =
            group.columns.some((column) => column.source === "name") ||
            group.columns.some((column) => column.source === "company.alias");
          const isSingleColumnGroup = group.colSpan === 1;
          const singleColumnAlign = group.columns[0]?.align || "left";
          const groupAlign = isSalesmanGroup
            ? "left"
            : isSingleColumnGroup
              ? singleColumnAlign
              : "center";
          const groupWidth = getGroupWidth(group);

          return (
            <TableCell
              key={`${group.label}-${index}`}
              align={groupAlign}
              colSpan={isSingleColumnGroup ? undefined : group.colSpan}
              rowSpan={isSingleColumnGroup ? 2 : undefined}
              className={isSalesmanGroup ? "si-group-sticky-salesman" : undefined}
              sx={{
                verticalAlign: isSingleColumnGroup ? "middle" : undefined,
                minWidth: groupWidth,
                width: groupWidth,
              }}
            >
              {hasProfitRatio ? (
                <Tooltip title={ratioTooltipText} placement="top">
                  <span
                    style={{
                      cursor: "help",
                      borderBottom: "1px dotted currentColor",
                    }}
                  >
                    {group.label}
                  </span>
                </Tooltip>
              ) : (
                group.label
              )}
            </TableCell>
          );
        })}
      </TableRow>

      {subheaderColumns.length > 0 ? (
        <TableRow className="si-subheader-row">
          {subheaderColumns.map((column) => (
            <TableCell
              key={column.source}
              align={column.align}
              className={column.headerClassName}
              sx={{
                minWidth: column.width,
                width: column.width,
              }}
            >
              {column.shortLabel}
            </TableCell>
          ))}
        </TableRow>
      ) : null}
    </TableHead>
  );
};

export default SalesmanIncentiveHeader;
